import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faCircleNotch,
  faExclamationCircle,
  faPause,
  faRobot,
  faWrench,
} from "@fortawesome/pro-regular-svg-icons";
import { Root as AccordionRoot } from "@radix-ui/react-accordion";
import { AnimatePresence, m } from "framer-motion";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

import {
  AgentSchemaPropertyUI,
  convertAgentSchemaBEtoUI,
} from "src/agents/AgentVersionPage/AgentSchemaEditor";
import {
  PlaygroundSessionStatus,
  usePlaygroundContext,
} from "src/agents/AgentVersionPage/PlaygroundContext";
import { useAgentSession } from "src/agents/queries/execution";
import { useAgentVersion } from "src/agents/queries/management";
import { Schema } from "src/api/flowTypes";
import { Button } from "src/base-components/Button";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { DateTimePickerDropdown } from "src/base-components/DateTimePickerDropdown";
import {
  accordionRootClassName,
  EditorAccordionItem,
} from "src/base-components/EditorAccordionItem";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FormItem } from "src/base-components/FormItem";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Select } from "src/base-components/Select";
import { Switch } from "src/base-components/Switch";
import { Textarea } from "src/base-components/Textarea";
import type {
  AIMessage,
  SessionResponse,
  SessionResponseMessagesInner,
  TerminationReason,
} from "src/clients/agent-management";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { toastActions } from "src/design-system/Toast/utils";
import { EnumValueObject } from "src/schema/schemaMappingUtils";
import { ErrorBoundary } from "src/utils/ErrorBoundary";

export type PlaygroundState =
  | { type: "input" }
  | {
      type: "results";
      // sessionId is null while waiting for invoke response, then set from backend
      sessionId: string | null;
      inputMessage: SessionResponseMessagesInner;
    };

type AgentPlaygroundContentProps = {
  agentId: string;
  versionId: string;
  agentName: string;
  playgroundId: string;
  playgroundState: PlaygroundState;
  onPlaygroundStateChange: (state: PlaygroundState) => void;
};

type AgentPlaygroundFormValues = {
  structuredInput: Record<string, unknown>;
  instructions: string;
};

export const AgentPlaygroundContent: React.FC<AgentPlaygroundContentProps> = ({
  agentId,
  versionId,
  agentName,
  playgroundState,
  onPlaygroundStateChange,
}) => {
  const [isInputAccordionExpanded, setIsInputAccordionExpanded] =
    useState(true);

  const versionQuery = useAgentVersion(agentId, versionId);
  // Get invoke from playground context - internally awaits any in-flight ping
  const {
    invoke: invokeAgent,
    sessionStatus,
    terminationReason,
  } = usePlaygroundContext();

  const sessionQuery = useAgentSession(
    playgroundState.type === "results" ? playgroundState.sessionId : null,
  );

  const inputSchema = versionQuery.data?.configuration.input_schema;

  const properties = useMemo(
    () =>
      convertAgentSchemaBEtoUI(inputSchema as Schema | null)?.properties ?? [],
    [inputSchema],
  );

  const playgroundForm = useForm<AgentPlaygroundFormValues>({
    defaultValues: generateDefaultValues(properties),
  });

  const prevPropertiesRef = useRef<AgentSchemaPropertyUI[]>(properties);

  // Reset form when input schema changes, preserving values for unchanged fields
  useEffect(() => {
    const prevProperties = prevPropertiesRef.current;
    const currentValues = playgroundForm.getValues();
    const newDefaults = generateDefaultValues(properties);
    const currentStructuredInput = currentValues.structuredInput;
    const newStructuredInput = newDefaults.structuredInput;

    // Merge: keep current value if field exists with same type (and still valid for enums), else use default
    const mergedStructuredInput: Record<string, unknown> = {};
    for (const prop of properties) {
      const prevProp = prevProperties.find(
        (p) => p.fieldName === prop.fieldName,
      );
      const propType = prop.type?.[0] ?? "string";
      const isSameType = prevProp?.type?.[0] === propType;
      const currentValue = currentStructuredInput[prop.fieldName];
      const enumValues = (prop.enum as EnumValueObject[] | undefined)?.map(
        (e) => e.value,
      );
      const isValidEnumValue =
        propType !== "enum" ||
        (enumValues ?? []).includes(currentValue as string);

      if (
        isSameType &&
        isValidEnumValue &&
        prop.fieldName in currentStructuredInput
      ) {
        mergedStructuredInput[prop.fieldName] = currentValue;
      } else {
        mergedStructuredInput[prop.fieldName] =
          newStructuredInput[prop.fieldName];
      }
    }

    playgroundForm.reset({
      structuredInput: mergedStructuredInput,
      instructions: currentValues.instructions,
    });
    prevPropertiesRef.current = properties;
  }, [properties, playgroundForm]);

  const isRunning = playgroundState.type === "results";
  const isTestInputFormDisabled = isRunning;

  // Expand accordion on reset to show test inputs again
  const prevIsRunningRef = useRef(isRunning);
  useEffect(() => {
    if (prevIsRunningRef.current && !isRunning) {
      // Was running, now not running (reset happened)
      setIsInputAccordionExpanded(true);
    }
    prevIsRunningRef.current = isRunning;
  }, [isRunning]);

  const handleSubmit = useCallback(
    (values: AgentPlaygroundFormValues) => {
      const messageContent = JSON.stringify(values);
      const inputMessage: SessionResponseMessagesInner = {
        id: uuidv4(),
        type: "human",
        content: messageContent,
      };

      // Set results state with null sessionId while waiting for invoke response
      onPlaygroundStateChange({
        type: "results",
        sessionId: null,
        inputMessage,
      });

      // Auto-collapse the input section when test starts
      setIsInputAccordionExpanded(false);

      invokeAgent.mutate(
        {
          messages: [{ role: "user", content: messageContent }],
        },
        {
          onSuccess: (response) => {
            // Update state with backend-generated sessionId to start polling
            onPlaygroundStateChange({
              type: "results",
              sessionId: response.session_id,
              inputMessage,
            });
          },
          onError: () => {
            // Reset to input state so user can retry
            onPlaygroundStateChange({ type: "input" });
            setIsInputAccordionExpanded(true);
            toastActions.failure({
              title: "Failed to run agent",
              description: "Please try again",
            });
          },
        },
      );
    },
    [invokeAgent, onPlaygroundStateChange],
  );

  return (
    <div className="flex h-full w-full flex-col">
      <ErrorBoundary
        fallbackErrorContent={
          <div className="flex flex-1 items-center justify-center p-5">
            <EmptyState
              description="Something went wrong. Please close and reopen the panel to try again."
              headline="Playground error"
              icon={faExclamationCircle}
              variant="error"
            />
          </div>
        }
      >
        <div className="decideScrollbar compensate-scrollbar-gutter flex h-full w-full flex-1 flex-col overflow-y-auto px-5">
          {/* Collapsible Test Input Section */}
          <div className="mt-4 flex-shrink-0">
            <AccordionRoot
              className={
                isRunning
                  ? "divide-y divide-gray-100 rounded-lg border border-gray-100"
                  : accordionRootClassName
              }
              type="single"
              value={isInputAccordionExpanded ? "test-input" : ""}
              collapsible
              onValueChange={(value) =>
                setIsInputAccordionExpanded(value === "test-input")
              }
            >
              <EditorAccordionItem
                className="pb-4 pl-[26px] pt-2"
                dataLoc="test-input-accordion"
                headerClassName={isRunning ? "text-gray-400" : undefined}
                title="Test input"
                value="test-input"
                forceMount
              >
                <div className="rounded-lg bg-gray-50 px-4 py-3">
                  <div className="space-y-3">
                    {properties.map((property) => (
                      <FormItem key={property.fieldName} gap="xs">
                        <FormItem.Label isRequired={property.required}>
                          {property.fieldName}
                        </FormItem.Label>
                        <PlaygroundInputField
                          control={playgroundForm.control}
                          disabled={isTestInputFormDisabled}
                          fieldPath={`structuredInput.${property.fieldName}`}
                          property={property}
                        />
                      </FormItem>
                    ))}
                    {/* Additional instructions field */}
                    <FormItem gap="xs">
                      <FormItem.Label>Agent instructions</FormItem.Label>
                      <Controller
                        control={playgroundForm.control}
                        name="instructions"
                        render={({ field }) => (
                          <Textarea
                            data-loc="test-input-instructions"
                            disabled={isTestInputFormDisabled}
                            placeholder="What should the agent take into account for this test run? (optional)"
                            value={field.value}
                            fullWidth
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </FormItem>
                  </div>
                </div>
              </EditorAccordionItem>
            </AccordionRoot>
          </div>

          {/* Run Button - only shown when not running */}
          {!isRunning && (
            <div className="mt-4 flex-shrink-0">
              <Button
                dataLoc="test-run-button"
                disabled={invokeAgent.isPending}
                loading={invokeAgent.isPending}
                onClick={async () => {
                  const isValid = await playgroundForm.trigger();
                  if (!isValid) {
                    // Expand accordion to show validation errors
                    setIsInputAccordionExpanded(true);
                    return;
                  }
                  handleSubmit(playgroundForm.getValues());
                }}
              >
                Run
              </Button>
            </div>
          )}

          {/* Results Section */}
          {isRunning && (
            <div className="mt-4 flex min-h-0 flex-1 flex-col">
              <ExecutionLogView
                agentName={agentName}
                messages={sessionQuery.data?.messages ?? []}
                optimisticInputMessage={playgroundState.inputMessage}
                result={sessionQuery.data?.result}
                sessionStatus={sessionStatus}
                terminationReason={terminationReason}
              />
            </div>
          )}
        </div>
      </ErrorBoundary>
    </div>
  );
};

type PlaygroundInputFieldProps = {
  property: AgentSchemaPropertyUI;
  control: Control<AgentPlaygroundFormValues>;
  fieldPath: `structuredInput.${string}`;
  disabled?: boolean;
};

const PlaygroundInputField: React.FC<PlaygroundInputFieldProps> = ({
  property,
  control,
  fieldPath,
  disabled = false,
}) => {
  const { fieldName, required } = property;
  const type = property.type?.[0] ?? "string";
  const enumValues = (property.enum as EnumValueObject[] | undefined)?.map(
    (e) => e.value,
  );

  switch (type) {
    case "string":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <Input
                data-loc={`test-input-${fieldName}`}
                disabled={disabled}
                placeholder={fieldName}
                value={(field.value as string) ?? ""}
                fullWidth
                onChange={field.onChange}
              />
              {fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) =>
                  (value as string)?.trim() !== "" || "Field is required"
              : undefined,
          }}
        />
      );

    case "number":
    case "integer":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <Input
                data-loc={`test-input-${fieldName}`}
                disabled={disabled}
                placeholder={fieldName}
                type="number"
                value={(field.value as string) ?? ""}
                fullWidth
                onChange={(e) => {
                  const val = e.target.value;
                  field.onChange(val === "" ? null : Number(val));
                }}
              />
              {fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) => value !== undefined || "Field is required"
              : undefined,
          }}
        />
      );

    case "boolean":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field }) => (
            <div className="inline-block px-2">
              <Switch
                dataLoc={`test-input-${fieldName}`}
                disabled={disabled}
                enabled={(field.value as boolean) ?? false}
                onChange={field.onChange}
              />
            </div>
          )}
        />
      );

    case "date":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <DateTimePickerDropdown
                disabled={disabled}
                placeholder={`Select ${fieldName}`}
                value={field.value as Date | undefined}
                allowFutureDates
                dateOnly
                onChange={field.onChange}
                onReset={() => field.onChange(null)}
              />
              {fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) => value !== undefined || "Field is required"
              : undefined,
          }}
        />
      );

    case "datetime":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <DateTimePickerDropdown
                disabled={disabled}
                placeholder={`Select ${fieldName}`}
                value={field.value as Date | undefined}
                allowFutureDates
                onChange={field.onChange}
                onReset={() => field.onChange(null)}
              />
              {fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) => value !== undefined || "Field is required"
              : undefined,
          }}
        />
      );

    case "enum":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <Select
                dataLoc={`test-input-${fieldName}`}
                disabled={disabled}
                options={(enumValues ?? []).map((val) => ({
                  key: val,
                  value: val,
                }))}
                placeholder={`Select ${fieldName}`}
                value={(field.value as string) ?? ""}
                onChange={field.onChange}
              />
              {fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) => value !== undefined || "Field is required"
              : undefined,
          }}
        />
      );

    case "array":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <div className="h-32">
                <CodeEditor
                  backgroundColor="white"
                  dataLoc={`test-input-${fieldName}`}
                  disabled={disabled}
                  language="json"
                  value={(field.value as string) ?? "[]"}
                  lineWrapping
                  onChange={(value) => {
                    field.onChange(value ?? "[]");
                  }}
                />
              </div>
              {fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: (value) => {
              if (!value) {
                return !required || "Field is required";
              }
              try {
                const parsed = JSON.parse(value as string);
                if (!Array.isArray(parsed)) {
                  return "Field is not an array";
                }
              } catch {
                return "Field is invalid JSON";
              }
            },
          }}
        />
      );

    case "object":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <div className="h-32">
                <CodeEditor
                  backgroundColor="white"
                  dataLoc={`test-input-${fieldName}`}
                  disabled={disabled}
                  language="json"
                  value={(field.value as string) ?? "{}"}
                  lineWrapping
                  onChange={(value) => {
                    field.onChange(value ?? "{}");
                  }}
                />
              </div>
              {fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: (value) => {
              if (!value) {
                return !required || "Field is required";
              }
              try {
                const parsed = JSON.parse(value as string);
                if (
                  typeof parsed !== "object" ||
                  parsed === null ||
                  Array.isArray(parsed)
                ) {
                  return "Field is not an object";
                }
                return true;
              } catch {
                return "Field is invalid JSON";
              }
            },
          }}
        />
      );

    default:
      return null;
  }
};

const generateDefaultValues = (
  properties: AgentSchemaPropertyUI[],
): AgentPlaygroundFormValues => {
  const structuredInput: Record<string, unknown> = {};
  for (const prop of properties) {
    const type = prop.type?.[0] ?? "string";
    switch (type) {
      case "string":
        structuredInput[prop.fieldName] = "";
        break;
      case "number":
      case "integer":
        structuredInput[prop.fieldName] = undefined;
        break;
      case "boolean":
        structuredInput[prop.fieldName] = false;
        break;
      case "enum":
        structuredInput[prop.fieldName] = undefined;
        break;
      case "date":
      case "datetime":
        structuredInput[prop.fieldName] = undefined;
        break;
      case "array":
        structuredInput[prop.fieldName] = "[]";
        break;
      case "object":
        structuredInput[prop.fieldName] = "{}";
        break;
    }
  }
  return {
    structuredInput,
    instructions: "",
  };
};

const formatTerminationReason = (reason: TerminationReason): string => {
  const reasonMap: Record<TerminationReason, string> = {
    expired: "Session expired",
    invocation_runtime_limit: "Runtime limit exceeded",
    model_call_limit: "Model call limit exceeded",
    tool_call_limit: "Tool call limit exceeded",
    infinite_tool_loop: "Infinite tool loop detected",
    error: "Internal error",
    user_cancelled: "Cancelled by user",
    upstream_cancelled: "Cancelled upstream",
    version_diverged: "Version diverged",
  };
  return reasonMap[reason] ?? reason;
};

const THINKING_VERBS = [
  "Thinking...",
  "Cogitating...",
  "Working...",
  "Wondering...",
  "Reflecting...",
  "Musing...",
  "Pondering...",
];
const VERB_ROTATION_INTERVAL_MS = 4500;

// Base component for displaying status with agent label (two-line layout)
type StatusIndicatorProps = {
  agentLabel: string;
  statusText: string;
  icon: React.ReactNode;
};

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  agentLabel,
  statusText,
  icon,
}) => {
  return (
    <m.div
      animate={{ opacity: 1 }}
      initial={{ opacity: 0 }}
      transition={{ duration: 0.15, delay: 0.1, ease: "easeOut" }}
    >
      <div className="flex flex-col gap-1">
        <span className="text-gray-500 font-inter-medium-11px">
          {agentLabel}
        </span>
        <div className="flex items-center gap-1.5">
          {icon}
          <AnimatePresence mode="wait">
            <m.span
              key={statusText}
              animate={{ opacity: 1 }}
              className="text-gray-800 font-inter-normal-12px"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeInOut" }}
            >
              {statusText}
            </m.span>
          </AnimatePresence>
        </div>
      </div>
    </m.div>
  );
};

// Thinking indicator with rotating text for booting/running states
type ThinkingStatusIndicatorProps = {
  agentLabel: string;
  status: PlaygroundSessionStatus | null;
};

const ThinkingStatusIndicator: React.FC<ThinkingStatusIndicatorProps> = ({
  agentLabel,
  status,
}) => {
  const [verbIndex, setVerbIndex] = useState(0);
  const isBooting = status === "booting";

  useEffect(() => {
    if (isBooting) {
      setVerbIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setVerbIndex((prev) => (prev + 1) % THINKING_VERBS.length);
    }, VERB_ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isBooting]);

  const statusText = isBooting ? "Booting..." : THINKING_VERBS[verbIndex];

  return (
    <StatusIndicator
      agentLabel={agentLabel}
      icon={
        <Icon
          color="text-indigo-500"
          icon={faCircleNotch}
          padding={false}
          size="xs"
          spin
        />
      }
      statusText={statusText}
    />
  );
};

// Wrapper component that dispatches to appropriate status display based on session state
type SessionStatusDisplayProps = {
  status: PlaygroundSessionStatus | null;
  agentLabel: string;
  terminationReason?: TerminationReason | null;
};

const SessionStatusDisplay: React.FC<SessionStatusDisplayProps> = ({
  status,
  agentLabel,
  terminationReason,
}) => {
  // Booting/Running: indigo spinner with rotating text
  if (status === "booting" || status === "running") {
    return <ThinkingStatusIndicator agentLabel={agentLabel} status={status} />;
  }

  // Paused: yellow pause icon with static text
  if (status === "paused") {
    return (
      <StatusIndicator
        agentLabel={agentLabel}
        icon={
          <Icon
            color="text-yellow-500"
            icon={faPause}
            padding={false}
            size="xs"
          />
        }
        statusText="Agent paused"
      />
    );
  }

  // Failed/Cancelled: full-width error callout
  if (status === "failed" || status === "cancelled") {
    let errorText: string;
    if (status === "failed" && terminationReason) {
      errorText = `Agent failed: ${formatTerminationReason(terminationReason)}`;
    } else if (status === "failed") {
      errorText = "Agent failed";
    } else {
      errorText = "Agent was cancelled";
    }

    return (
      <Callout type={status === "failed" ? "error" : "neutral"}>
        {errorText}
      </Callout>
    );
  }

  // Success or null: nothing to show
  return null;
};

type ExecutionLogViewProps = {
  agentName: string;
  sessionStatus: PlaygroundSessionStatus | null;
  terminationReason?: TerminationReason | null;
  messages: SessionResponseMessagesInner[];
  optimisticInputMessage: SessionResponseMessagesInner;
  result: SessionResponse["result"];
};

const ExecutionLogView: React.FC<ExecutionLogViewProps> = ({
  agentName,
  sessionStatus,
  terminationReason,
  messages: rawMessages,
  optimisticInputMessage,
  result,
}) => {
  // Filter out human messages from session (we show the optimistic input separately)
  const agentMessages = useMemo(() => {
    return rawMessages.filter((msg) => msg.type !== "human");
  }, [rawMessages]);

  // Show status display for all non-success states
  const showStatusDisplay =
    sessionStatus !== null && sessionStatus !== "success";
  const hasInstructions = !!(
    JSON.parse(
      optimisticInputMessage.content as string,
    ) as AgentPlaygroundFormValues
  ).instructions?.trim();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  // Track scroll position to determine if user is at bottom
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Consider "at bottom" if within 50px of the bottom
    isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 50;
  }, []);

  // Auto-scroll when new messages arrive (only if user was at bottom)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (isAtBottomRef.current) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [agentMessages.length]);

  // Initial scroll to bottom
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div
        ref={scrollContainerRef}
        className="decideScrollbar compensate-scrollbar-gutter flex-1 space-y-3 overflow-y-auto py-4"
        onScroll={handleScroll}
      >
        {/* User message at top - only shown if instructions are non-empty */}
        {hasInstructions && (
          <m.div
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.15, delay: 0.1, ease: "easeOut" }}
          >
            <ExecutionLogEntry message={optimisticInputMessage} />
          </m.div>
        )}

        {/* Agent messages */}
        {agentMessages.map((message, index) => (
          <m.div
            key={message.id ?? `msg-${index}`}
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.15, delay: 0.1, ease: "easeOut" }}
          >
            <ExecutionLogEntry message={message} />
          </m.div>
        ))}

        {/* Session status display (thinking, paused, or error) */}
        {showStatusDisplay && (
          <SessionStatusDisplay
            agentLabel={agentName}
            status={sessionStatus}
            terminationReason={terminationReason}
          />
        )}

        {/* Final result */}
        {result && (
          <m.div
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.15, delay: 0.1, ease: "easeOut" }}
          >
            <div className="flex min-w-0 items-start gap-2">
              <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100">
                <Icon color="text-indigo-600" icon={faRobot} size="xs" />
              </div>
              <div className="min-w-0 flex-1 rounded-lg border border-green-200 bg-green-50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Icon color="text-green-600" icon={faCheck} size="sm" />
                  <span className="text-green-700 font-inter-semibold-12px">
                    Complete
                  </span>
                </div>
                <div className="min-w-0 text-gray-700">
                  {result.text && (
                    <p className="whitespace-pre-wrap font-inter-normal-12px">
                      {result.text}
                    </p>
                  )}
                  {result.structured_output && (
                    <div className="mt-2 rounded bg-white/50 p-2">
                      <CollapsiblePre>
                        {JSON.stringify(result.structured_output, null, 2)}
                      </CollapsiblePre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </m.div>
        )}
      </div>
    </div>
  );
};

const COLLAPSED_MAX_HEIGHT = 80;
const SCROLLBAR_HEIGHT = 15;

type CollapsiblePreProps = {
  children: string;
  className?: string;
};

const CollapsiblePre: React.FC<CollapsiblePreProps> = ({
  children,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef<HTMLPreElement>(null);

  const needsExpansion = contentHeight > COLLAPSED_MAX_HEIGHT;

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [children]);

  return (
    <div className="relative">
      <m.div
        animate={{
          height:
            (isExpanded
              ? contentHeight
              : Math.min(contentHeight, COLLAPSED_MAX_HEIGHT)) +
            SCROLLBAR_HEIGHT,
        }}
        className="overflow-hidden"
        initial={false}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      >
        <pre
          ref={contentRef}
          className={`decideScrollbar overflow-x-auto text-xs ${className ?? ""}`}
        >
          {children}
        </pre>
      </m.div>
      {needsExpansion && (
        <button
          className="mt-1 flex items-center gap-1 text-gray-500 font-inter-medium-11px hover:text-gray-700"
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Icon
            color="text-gray-500"
            icon={isExpanded ? faChevronUp : faChevronDown}
            size="2xs"
          />
          {isExpanded ? "Collapse" : "Expand"}
        </button>
      )}
    </div>
  );
};

type ExecutionLogEntryProps = {
  message: SessionResponseMessagesInner;
};

const ExecutionLogEntry: React.FC<ExecutionLogEntryProps> = ({ message }) => {
  const content =
    typeof message.content === "string"
      ? message.content
      : JSON.stringify(message.content);
  const toolCalls: Exclude<AIMessage["tool_calls"], undefined> =
    message.tool_calls ?? [];

  // Human message - show the input
  if (message.type === "human") {
    const parsed = JSON.parse(content) as AgentPlaygroundFormValues;
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
        <p className="text-gray-500 font-inter-medium-11px">Input</p>
        <p className="mt-1 break-words text-gray-700 font-inter-normal-12px">
          {parsed.instructions}
        </p>
      </div>
    );
  }

  // AI message with tool calls - show as "calling tool" entry
  if (message.type === "ai" && toolCalls.length > 0) {
    return (
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100">
          <Icon color="text-indigo-600" icon={faRobot} size="xs" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {content && (
            <p className="text-gray-700 font-inter-normal-12px">{content}</p>
          )}
          {toolCalls.map((toolCall) => (
            <div
              key={toolCall.id}
              className="overflow decideScrollbar flex max-w-full items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2"
            >
              <Icon color="text-indigo-500" icon={faWrench} size="xs" />
              <span className="font-mono text-xs text-indigo-700">
                {toolCall.name}
              </span>
              <span className="min-w-0 overflow-hidden text-ellipsis font-mono text-2xs text-indigo-500">
                (
                {Object.entries(toolCall.args)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join(", ")}
                )
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Tool result message
  if (message.type === "tool") {
    let parsedContent: unknown;
    try {
      parsedContent = JSON.parse(content);
    } catch {
      parsedContent = content;
    }

    return (
      <div className="ml-7 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
        <div className="flex items-center gap-2">
          <Icon color="text-green-600" icon={faCheck} size="xs" />
          <span className="text-gray-600 font-inter-medium-12px">
            Tool result
          </span>
        </div>
        <CollapsiblePre className="mt-1 text-gray-600">
          {typeof parsedContent === "object"
            ? JSON.stringify(parsedContent, null, 2)
            : (parsedContent as string)}
        </CollapsiblePre>
      </div>
    );
  }

  // AI message without tool calls - show as thinking/intermediate response
  if (message.type === "ai") {
    return (
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100">
          <Icon color="text-indigo-600" icon={faRobot} size="xs" />
        </div>
        <p className="text-gray-700 font-inter-normal-12px">{content}</p>
      </div>
    );
  }

  // Unknown message type - don't render
  return null;
};
